import React, { useEffect, useRef } from "react";

/**
 * Official 3Dash Quote + Checkout widget.
 *
 * Docs (from https://3dash.in/q.js source):
 *   <script src="https://3dash.in/q.js"
 *           data-shop="pk_live_..."   // publishable key
 *           data-theme="midnight"     // widget skin
 *           data-plan="checkout"      // "quote" or "checkout"
 *           data-target="#mount"      // OPTIONAL: mount into this selector
 *           async></script>
 *
 * The script renders an iframe pointing at /embed inline. If `data-target`
 * is given, the iframe is appended to that element; otherwise it's inserted
 * right before the <script> tag in the DOM.
 *
 * This component renders a mount div and injects a script tag *inside* it
 * so 3Dash anchors the iframe exactly where we want it (never a floating
 * element). Env var REACT_APP_3DASH_SHOP holds the publishable key.
 */

// 3Dash presets (see https://3dash.in/widget-themes.js):
//   default (yellow), studio (blue on dark), sand, mint, carbon, midnight
// "carbon" = warm near-black with an orange accent — matches this site's brand.
export const DEFAULT_THEME = "carbon";
export const DEFAULT_PLAN = "checkout";

const SCRIPT_ID = "threedash-embed-script";
const MOUNT_ID = "threedash-embed-mount";

export default function QuoteWidget({
  shopId,
  theme = DEFAULT_THEME,
  plan = DEFAULT_PLAN,
}) {
  const mountRef = useRef(null);
  const finalShop = shopId || process.env.REACT_APP_3DASH_SHOP;

  useEffect(() => {
    if (!finalShop || !mountRef.current) return;
    // Avoid double-injecting on hot reload / re-mount.
    if (document.getElementById(SCRIPT_ID)) return;

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = "https://3dash.in/q.js";
    script.async = true;
    script.dataset.shop = finalShop;
    script.dataset.theme = theme;
    script.dataset.plan = plan;
    script.dataset.target = `#${MOUNT_ID}`;
    mountRef.current.appendChild(script);
  }, [finalShop, theme, plan]);

  if (!finalShop) {
    return (
      <div
        data-testid="quote-widget-missing-key"
        className="border border-white/10 bg-[#0F0F11] rounded-2xl p-8 text-center text-zinc-400"
      >
        <p className="text-sm font-mono uppercase tracking-[0.2em] text-[#FF5722] mb-2">
          Widget offline
        </p>
        <p className="text-sm">
          Set <code className="text-white">REACT_APP_3DASH_SHOP</code> in{" "}
          <code className="text-white">/app/frontend/.env</code> with your
          3Dash publishable key.
        </p>
      </div>
    );
  }

  return (
    <div
      ref={mountRef}
      id={MOUNT_ID}
      data-testid="quote-widget-mount"
      className="w-full min-h-[520px] rounded-2xl overflow-hidden border border-white/10 bg-[#0F0F11]"
    />
  );
}
